/* eslint-disable no-unused-vars */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { productService } from '../../services/api'
import { Product } from '../../models/Product.model'

const initialState = {
  products: {},
  categories: {},
  currentProduct: null,
  loading: false,
  error: null,
  filters: {
    search: '',
    categoryId: null,
    productType: null,
    productKind: null,
    priceRange: [0, 1000]
  },
  pagination: {
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 15
  },
  categoriesPagination: {
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  }
}

const productToSerializable = (product) => {
  if (!product) return null
  
  // Se já é um objeto plano, retornar como está
  if (!(product instanceof Product)) {
    return product
  }
  
  // Converter ProductLang para objetos planos
  const ptBR = product.ptBR ? {
    name: product.ptBR.name || '',
    description: product.ptBR.description || '',
    price: product.ptBR.price || 0,
    discount: product.ptBR.discount || 0,
    publish: product.ptBR.publish || false
  } : { name: '', description: '', price: 0, discount: 0, publish: false }
  
  const en = product.en ? {
    name: product.en.name || '',
    description: product.en.description || '',
    price: product.en.price || 0,
    discount: product.en.discount || 0,
    publish: product.en.publish || false
  } : { name: '', description: '', price: 0, discount: 0, publish: false }
  
  const es = product.es ? {
    name: product.es.name || '',
    description: product.es.description || '',
    price: product.es.price || 0,
    discount: product.es.discount || 0,
    publish: product.es.publish || false
  } : { name: '', description: '', price: 0, discount: 0, publish: false }
  
      // Converter Product para objeto plano
    const serializableProduct = {
      id: product.id,
      productType: product.productType,
      productKind: product.productKind,
      productCategory: product.productCategory || product.category?.id || 0,
      code: product.code,
      image: product.image,
      manufactureTime: product.manufactureTime,
      requiresManufacture: product.requiresManufacture,
      askBdsPrinting: product.askBdsPrinting,
      qtdOption: product.qtdOption,
      qtdOptionName: product.qtdOptionName,
      qtdMax: product.qtdMax,
      alignment: product.alignment,
      favorite: product.favorite,
      kindOfManufacture: product.kindOfManufacture,
      enableKit: product.enableKit,
      enableSleeve: product.enableSleeve,
      enableR2: product.enableR2,
      createdAt: product.createdAt,
      modifiedAt: product.modifiedAt,
      deletedAt: product.deletedAt,
      uploadFiles: product.uploadFiles || [],
      downloadFiles: product.downloadFiles || [],
      ptBR,
      en,
      es,
      price: product.price,
    discount: product.discount,
    currency: product.currency,
    // Preservar preços regionais da API
    prices: product.prices || null,
    pricePtBr: product.pricePtBr,
    priceEn: product.priceEn,
    priceEs: product.priceEs,
    namePtBr: product.namePtBr,
    nameEn: product.nameEn,
    nameEs: product.nameEs,
      category: product.category || null,
      name: product.name,
      description: product.description,
      shortDescription: product.shortDescription,
      features: product.features || [],
      specifications: product.specifications || {},
      images: product.images || [],
      published: product.published,
      serviceDataDefault: product.serviceDataDefault || {},
      typeName: product.typeName,
      kindName: product.kindName,
      // Adicionar protocolos que estavam faltando
      fileProtocols: product.fileProtocols || [],
      // Converter comboData para objeto plano se existir
      comboData: product.comboData ? {
      id: product.comboData.id,
      name: product.comboData.name,
      description: product.comboData.description,
      items: product.comboData.items ? product.comboData.items.map(item => ({
        id: item.id,
        productId: item.productId,
        quantity: item.quantity,
        required: item.required,
        discount: item.discount
      })) : []
    } : null,
    // Resumo (nome/foto/qtd) dos produtos internos do combo, pra listagem
    comboContents: product.comboContents || [],
    // Converter manufactureData para objeto plano se existir
    manufactureData: product.manufactureData ? {
      id: product.manufactureData.id,
      productId: product.manufactureData.productId,
      materialType: product.manufactureData.materialType,
      materialTypeName: product.manufactureData.materialTypeName,
      materialSpecifications: product.manufactureData.materialSpecifications,
      complexityLevel: product.manufactureData.complexityLevel,
      complexityLevelName: product.manufactureData.complexityLevelName,
      estimatedTime: product.manufactureData.estimatedTime,
      specialHandling: product.manufactureData.specialHandling,
      notes: product.manufactureData.notes
    } : null,
    quantity: product.quantity,
    innerCheckoutProducts: product.innerCheckoutProducts || []
  }
  
  console.log('📡 Serializable Product:', serializableProduct)
  
  return serializableProduct
}

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (filters = {}, { rejectWithValue }) => {
    try {
      // Filtrar parâmetros vazios
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => {
          if (value === null || value === undefined || value === '') return false
          return true
        })
      )
      
      const response = await productService.getAll(cleanFilters)
      const products = response.data.obj?.products || []
      
      // Converter para objetos serializáveis
      const serializableProducts = products.map(p => {
        const product = Product.FromObj(p)
        return productToSerializable(product)
      })
      
      return {
        products: serializableProducts,
        pagination: response.data.obj?.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 12
        }
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products')
    }
  }
)

export const fetchProductsList = createAsyncThunk(
  'products/fetchProductsList',
  async (filters = {}, { rejectWithValue }) => {
    try {
      // Filtrar parâmetros vazios
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => {
          if (value === null || value === undefined || value === '') return false
          return true
        })
      )
      
      // Converter campos camelCase para snake_case para a API
      const apiFilters = {}
      Object.entries(cleanFilters).forEach(([key, value]) => {
        switch (key) {
          case 'categoryId':
            apiFilters.category_id = value
            break
          case 'productType':
            apiFilters.product_type = value
            break
          case 'productKind':
            apiFilters.product_kind = value
            break
          case 'perPage':
            apiFilters.per_page = value
            break
          case 'per_page':
            apiFilters.per_page = value
            break
          case 'currentPage':
            apiFilters.page = value
            break
          default:
            apiFilters[key] = value
        }
      })
      
      const response = await productService.getList(apiFilters)
      const products = response.data.obj?.products || []
      
      // Converter para objetos serializáveis
      const serializableProducts = products.map(p => {
        const product = Product.FromObj(p)
        return productToSerializable(product)
      })
      
      return {
        products: serializableProducts,
        pagination: response.data.obj?.pagination || {
          current_page: 1,
          last_page: 1,
          total: 0,
          per_page: 15
        }
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products list')
    }
  }
)

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productService.getById(productId)
      const rawData = response.data.obj?.product || response.data.obj || response.data
      
      console.log('📡 Raw API Data:', rawData)
      
      // Usar os dados diretamente da API sem mapeamento desnecessário
      const product = Product.FromObj(rawData)
      const serializedProduct = productToSerializable(product)
      
      console.log('📡 Serializable Product:', serializedProduct)
      
      return serializedProduct
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product')
    }
  }
)

export const fetchProductDetails = createAsyncThunk(
  'products/fetchProductDetails',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productService.getDetails(productId)
      const rawData = response.data.obj
      console.log('📡 Raw Data:', rawData)
      
      // Retornar dados mapeados diretamente para a view (sem passar por toFormData que perde dados)
      return {
        id: rawData.id,
        code: rawData.code,
        image: rawData.image,
        // Manter category como objeto completo para getCategoryLocalizedName funcionar
        category: rawData.category,
        // Dados localizados
        data_pt_br: rawData.dataPtBr,
        data_en: rawData.dataEn,
        data_es: rawData.dataEs,
        // Nomes legíveis para exibição
        type_name: rawData.typeName,
        kind_name: rawData.kindName,
        qtd_option_name: rawData.qtdOptionName,
        // IDs numéricos
        product_type: rawData.productType,
        product_kind: rawData.productKind,
        qtd_option: rawData.qtdOption,
        qtd_max: rawData.qtdMax,
        // Configurações
        manufacture_time: rawData.manufactureTime,
        requires_manufacture: rawData.requiresManufacture,
        ask_bds_printing: rawData.askBdsPrinting ?? rawData.ask_bds_printing ?? false,
        alignment: rawData.alignment,
        enable_kit: rawData.enableKit ?? rawData.enable_kit,
        enable_sleeve: rawData.enableSleeve ?? rawData.enable_sleeve,
        enable_r2: rawData.enableR2 ?? rawData.enable_r2,
        // Preços
        price_pt_br: rawData.pricePtBr,
        price_en: rawData.priceEn,
        price_es: rawData.priceEs,
        // Publicação
        publish_pt_br: rawData.publishPtBr,
        publish_en: rawData.publishEn,
        publish_es: rawData.publishEs,
        // Protocolos e dados adicionais
        file_protocols: rawData.fileProtocols || rawData.file_protocol || [],
        product_data: rawData.productData,
        service_data_default: rawData.serviceDataDefault,
        // Combo: produtos internos e preço sugerido (soma dos internos)
        inner_products: rawData.innerProducts || [],
        suggested_price: rawData.suggestedPrice || null,
        // Timestamps
        created_at: rawData.createdAt,
        updated_at: rawData.updatedAt
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product details')
    }
  }
)

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const response = await productService.create(productData)
      const product = Product.FromObj(response.data.obj || response.data)
      return productToSerializable(product)
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create product')
    }
  }
)

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const response = await productService.update(productId, productData)
      const product = Product.FromObj(response.data.obj || response.data)
      return productToSerializable(product)
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update product')
    }
  }
)

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (productId, { rejectWithValue }) => {
    try {
      await productService.delete(productId)
      return productId
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete product')
    }
  }
)

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await productService.getCategories(filters)
      const data = response.data.obj || response.data || {}
      
      // Nova estrutura da API: data.data (array) + paginação
      if (data.data && Array.isArray(data.data)) {
        return {
          categories: data.data,
          pagination: {
            currentPage: data.current_page || 1,
            totalPages: data.last_page || 1,
            totalItems: data.total || 0,
            itemsPerPage: data.per_page || 15,
            from: data.from || 1,
            to: data.to || 15
          }
        }
      }
      
      // Se é apenas um array (compatibilidade com versão anterior)
      if (Array.isArray(data)) {
        return {
          categories: data,
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: data.length,
            itemsPerPage: data.length
          }
        }
      }
      
      // Fallback
      return {
        categories: [],
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        }
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch categories')
    }
  }
)

export const fetchAllCategories = createAsyncThunk(
  'products/fetchAllCategories',
  async (search = '', { rejectWithValue }) => {
    try {
      const response = await productService.getAllCategories(search)
      const data = response.data.obj?.categories || response.data || []
      
      // Se é um array direto
      if (Array.isArray(data)) {
        return data
      }
      
      // Se tem estrutura com data
      if (data.data && Array.isArray(data.data)) {
        return data.data
      }
      
      // Fallback
      return []
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch all categories')
    }
  }
)

export const searchProducts = createAsyncThunk(
  'products/searchProducts',
  async (query, { rejectWithValue }) => {
    try {
      const response = await productService.search(query)
      const products = response.data.obj?.products || []
      
      // Converter para objetos serializáveis
      const serializableProducts = products.map(p => {
        const product = Product.FromObj(p)
        return productToSerializable(product)
      })
      
      return serializableProducts
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search products')
    }
  }
)

export const createCategory = createAsyncThunk(
  'products/createCategory',
  async (categoryData, { rejectWithValue }) => {
    try {
      const response = await productService.createCategory(categoryData)
      return response.data.obj || response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create category')
    }
  }
)

export const updateCategory = createAsyncThunk(
  'products/updateCategory',
  async ({ id, categoryData }, { rejectWithValue }) => {
    try {
      const response = await productService.updateCategory(id, categoryData)
      return response.data.obj || response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update category')
    }
  }
)

export const deleteCategory = createAsyncThunk(
  'products/deleteCategory',
  async (categoryId, { rejectWithValue }) => {
    try {
      await productService.deleteCategory(categoryId)
      return categoryId
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete category')
    }
  }
)

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        categoryId: null,
        productType: null,
        productKind: null,
        priceRange: [0, 1000]
      }
    },
    setCurrentProduct: (state, action) => {
      state.currentProduct = action.payload
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false
        // Não limpar produtos existentes, apenas adicionar/atualizar
        action.payload.products.forEach(product => {
          state.products[product.id] = product
        })
        state.pagination = action.payload.pagination
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(fetchProductsList.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProductsList.fulfilled, (state, action) => {
        state.loading = false
        // Limpar produtos existentes e substituir pelos novos (filtrados)
        state.products = {}
        action.payload.products.forEach(product => {
          state.products[product.id] = product
        })
        state.pagination = action.payload.pagination
      })
      .addCase(fetchProductsList.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false
        state.currentProduct = action.payload
        state.products[action.payload.id] = action.payload
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(fetchProductDetails.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProductDetails.fulfilled, (state, action) => {
        state.loading = false
        state.currentProduct = action.payload
        state.products[action.payload.id] = action.payload
      })
      .addCase(fetchProductDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(createProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createProduct.fulfilled, (state) => {
        state.loading = false
        // Não adicionar produto localmente - será recarregado da API na listagem
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(updateProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProduct.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loading = false
        delete state.products[action.payload]
        if (state.currentProduct && state.currentProduct.id === action.payload) {
          state.currentProduct = null
        }
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false
        state.categories = {}
        
        // Se a resposta tem paginação
        if (action.payload.categories && action.payload.pagination) {
          action.payload.categories.forEach(category => {
            state.categories[category.id] = category
          })
          state.categoriesPagination = action.payload.pagination
        } else {
          // Compatibilidade com versão anterior (array simples)
          action.payload.forEach(category => {
            state.categories[category.id] = category
          })
        }
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(fetchAllCategories.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllCategories.fulfilled, (state, action) => {
        state.loading = false
        state.categories = {}
        
        // Adicionar todas as categorias ao estado
        action.payload.forEach(category => {
          state.categories[category.id] = category
        })
      })
      .addCase(fetchAllCategories.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(searchProducts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.loading = false
        // Update products with search results
        action.payload.forEach(product => {
          state.products[product.id] = product
        })
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(createCategory.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false
        // Não adicionar ao estado local, pois será recarregado do backend
        // state.categories[action.payload.id] = action.payload
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(updateCategory.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false
        // Não atualizar no estado local, pois será recarregado do backend
        // state.categories[action.payload.id] = action.payload
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false
        // Não remover do estado local, pois será recarregado do backend
        // delete state.categories[action.payload]
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const {
  clearError,
  setFilters,
  clearFilters,
  setCurrentProduct,
  clearCurrentProduct,
  setLoading,
  setPagination
} = productsSlice.actions

// Selectors
export const selectProducts = (state) => state.products.products
export const selectCategories = (state) => state.products.categories
export const selectCurrentProduct = (state) => state.products.currentProduct
export const selectProductsLoading = (state) => state.products.loading
export const selectProductsError = (state) => state.products.error
export const selectProductsFilters = (state) => state.products.filters
export const selectProductsPagination = (state) => state.products.pagination
export const selectCategoriesPagination = (state) => state.products.categoriesPagination

// Funções utilitárias para acessar dados do produto
export const getProductName = (product, locale = 'pt') => {
  if (!product) return ''

  // Nome no idioma da UI (não no idioma/moeda da loja do usuário), com
  // fallback pros outros idiomas cadastrados quando o preferido está vazio.
  const localeFieldMap = { pt: 'namePtBr', en: 'nameEn', es: 'nameEs' }
  const preferredField = localeFieldMap[locale] || 'namePtBr'

  return (
    product[preferredField] ||
    product.namePtBr ||
    product.nameEn ||
    product.nameEs ||
    product.ptBR?.name ||
    product.en?.name ||
    product.es?.name ||
    product.name ||
    ''
  )
}

export const getProductPrice = (product, locale = 'pt') => {
  if (!product) return 0
  
  // Se o produto tem dados localizados, usar eles
  if (product.ptBR || product.en || product.es) {
    const localeMap = {
      pt: product.ptBR,
      en: product.en,
      es: product.es
    }
    const localizedData = localeMap[locale] || product.ptBR
    return localizedData?.price || product.price || 0
  }
  
  // Caso contrário, usar os dados diretos da API
  return product.price || 0
}

export const getProductDescription = (product, locale = 'pt') => {
  if (!product) return ''
  
  // Se o produto tem dados localizados, usar eles
  if (product.ptBR || product.en || product.es) {
    const localeMap = {
      pt: product.ptBR,
      en: product.en,
      es: product.es
    }
    const localizedData = localeMap[locale] || product.ptBR
    return localizedData?.description || product.description || ''
  }
  
  // Caso contrário, usar os dados diretos da API
  return product.description || ''
}

export const getProductDiscount = (product, locale = 'pt') => {
  if (!product) return 0
  
  // Se o produto tem dados localizados, usar eles
  if (product.ptBR || product.en || product.es) {
    const localeMap = {
      pt: product.ptBR,
      en: product.en,
      es: product.es
    }
    const localizedData = localeMap[locale] || product.ptBR
    return localizedData?.discount || product.discount || 0
  }
  
  // Caso contrário, usar os dados diretos da API
  return product.discount || 0
}

export const getProductFinalPrice = (product, locale = 'pt') => {
  const price = getProductPrice(product, locale)
  const discount = getProductDiscount(product, locale)
  
  if (discount > 0) {
    // eslint-disable-next-line no-mixed-operators
    return price * (1 - discount / 100)
  }
  
  return price
}

export const hasProductDiscount = (product, locale = 'pt') => {
  return getProductDiscount(product, locale) > 0
}

export const getProductSavings = (product, locale = 'pt') => {
  const price = getProductPrice(product, locale)
  const finalPrice = getProductFinalPrice(product, locale)
  return price - finalPrice
}

export const isProductPublished = (product, locale = 'pt') => {
  if (!product) return false
  
  // Se o produto tem dados localizados, usar eles
  if (product.ptBR || product.en || product.es) {
    const localeMap = {
      pt: product.ptBR,
      en: product.en,
      es: product.es
    }
    const localizedData = localeMap[locale] || product.ptBR
    return localizedData?.publish || product.published || false
  }
  
  // Caso contrário, usar os dados diretos da API
  return product.published || false
}

export const selectProductById = (state, productId) => state.products.products[productId]
export const selectProductsByCategory = (categoryId) => (state) => {
  return Object.values(state.products.products).filter(p => p.productCategory === categoryId)
}

export const selectFilteredProducts = (state) => {
  const products = Object.values(state.products.products)
  const { search, categoryId, productType, productKind, priceRange } = state.products.filters
  
  return products.filter(p => {
    // Filtro por nome/código
    if (search) {
      const searchLower = search.toLowerCase()
      const nameMatch = p.name && p.name.toLowerCase().includes(searchLower)
      const codeMatch = p.code && p.code.toLowerCase().includes(searchLower)
      const descriptionMatch = p.description && p.description.toLowerCase().includes(searchLower)
      
      if (!nameMatch && !codeMatch && !descriptionMatch) return false
    }
    
    // Filtro por categoria
    if (categoryId && categoryId !== 'all' && p.productCategory !== categoryId) return false
    
    // Filtro por tipo de produto
    if (productType && p.productType !== productType) return false
    
    // Filtro por tipo de produto (planejamento/manufatura)
    if (productKind && p.productKind !== productKind) return false
    
    // Filtro por faixa de preço (só se o produto tem preço)
    if (p.price && (p.price < priceRange[0] || p.price > priceRange[1])) return false
    
    return true
  })
}

export const selectProductsCount = (state) => {
  return Object.keys(state.products.products).length
}

export const selectCategoriesCount = (state) => {
  return Object.keys(state.products.categories).length
}

export default productsSlice.reducer
